import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input } from '@angular/core';
import {PersonLogin, RoleName} from '../../person/person-login';
import {PersonLoginService} from '../../services/person-login-service';
import {ProductService} from '../../services/product-service';
import { Router } from '@angular/router';
import {SpecificationCharacteristicService} from '../../services/specification-characteristic.service';
import {StringUtils} from '../../utils/string-utils';
import {Product} from '../product';
import {MatSnackBar} from '@angular/material/snack-bar';
import { firstValueFrom, Observable } from "rxjs";
import {FormsModule, ReactiveFormsModule, UntypedFormControl} from '@angular/forms';
import {map, startWith} from 'rxjs/operators';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatInput} from '@angular/material/input';
import {MatOption} from '@angular/material/core';
import {MatAutocomplete, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import { AsyncPipe } from '@angular/common';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {BrandID} from "../../enums/brand-id";
import { HttpErrorResponse } from "@angular/common/http";

@Component({
    selector: 'app-product-sku-slots',
    templateUrl: './product-sku-slots.component.html',
    styleUrls: ['./product-sku-slots.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatIcon, MatAutocomplete, MatOption, MatInput, FormsModule, MatAutocompleteTrigger, ReactiveFormsModule, MatProgressSpinner, AsyncPipe]
})
export class ProductSkuSlotsComponent implements OnInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly productService = inject(ProductService);
  private readonly router = inject(Router);
  private readonly specificationCharacteristicService = inject(SpecificationCharacteristicService);
  private readonly snackBar = inject(MatSnackBar);
  private allSlots: string[] = [];
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly productId = input.required<string>();
  personLogin!: PersonLogin;
  product!: Product;
  readonly availableSlots = signal<string[]>(undefined!);
  readonly existingSlots = signal<string[]>(undefined!);
  readonly newSlotName = signal<string>(undefined!);
  readonly selectedExistingSlot = signal<string>(undefined!);
  filteredAvailable!: Observable<string[]>;
  availableSkuInput = new UntypedFormControl('');

  private handleError(error: HttpErrorResponse): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }

  ngOnInit(): void {
    if (!this.personLoginService.hasRole(RoleName.IT)) {
      this.router.navigate(['main']).then(() => this.isBusy.set(false));
    }
    this.personLogin = this.personLoginService.getCachedLogin()!;
    if (!this.productId() || this.productId().trim().length === 0) {
      this.backToProductSearch();
    }
    this.isBusy.set(true);
    this.initialize().then(() => this.isBusy.set(false), (error: HttpErrorResponse) => this.handleError(error));
  }

  private async initialize(): Promise<void> {
    this.product = await firstValueFrom<Product>(this.productService.loadProduct(this.productId(), BrandID.CLM));
    this.existingSlots.set(await firstValueFrom<string[]>(this.productService.getProductSkuSlots(this.productId())));
    this.allSlots = await firstValueFrom<string[]>(this.specificationCharacteristicService.getAllSkuSlots());
    this.filterAvailableSlots();
    this.filteredAvailable = this.availableSkuInput.valueChanges.pipe(
      startWith(''),
      map(value => this.filterAvailable(value || '')),
    );
  }

  private filterAvailable(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.availableSlots().filter(slot => slot.toLowerCase().includes(filterValue));
  }

  private backToProductSearch(): void {
    this.router.navigate(['product']).then(() => this.isBusy.set(false));
  }

  backToProductDetail(): void {
    this.router.navigate(['product', this.productId(), this.product.brandID]).then(() => this.isBusy.set(false));
  }

  availableSkuSelected(value: string | undefined): void {
    this.newSlotName.set(value!);
  }

  saveProductSkuSlots(): void {
    this.isBusy.set(true);
    this.productService.saveProductSkuSlots(this.productId(), this.existingSlots()).subscribe({
      error: (error) => this.handleError(error),
      complete: () => {
        this.selectedExistingSlot.set(undefined!);
        this.newSlotName.set(undefined!);
        this.isBusy.set(false);
        this.snackBar.open('Successfully updated', 'Close');
      }
    });
  }

  addSlot(): void {
    if (!this.existingSlots()) {
      this.existingSlots.set([]);
    }
    if (this.newSlotName()?.trim()?.length > 0) {
      if (this.existingSlots().filter(slot => slot === this.newSlotName()).length === 0) {
        this.existingSlots.update(current => [...current, this.newSlotName()]);
      }
    }
    this.newSlotName.set('');
    this.filterAvailableSlots();
    this.availableSkuInput.setValue('');
  }

  private filterAvailableSlots(): void {
    this.availableSlots.set([]);
    if (this.allSlots) {
      if ((this.existingSlots()?.length || 0) === 0) {
       this.existingSlots.set([]);
      }
      this.availableSlots.set(this.allSlots.filter((slot: string) => this.existingSlots().indexOf(slot) < 0));
    }
  }

  removeExistingSlot(): void {
    if (this.selectedExistingSlot() && this.selectedExistingSlot().trim().length > 0) {
      const index = this.existingSlots().indexOf(this.selectedExistingSlot());
      if (index >= 0) {
        this.existingSlots.update(current => { const next = [...current]; next.splice(index, 1); return next; });
        this.selectedExistingSlot.set(undefined!);
        this.filterAvailableSlots();
      }
    }
  }

}
